import { memoryUtils } from '@fs/zion-memories/memories-authorized-use-only'
import { getEventCoordinates } from '@dnd-kit/utilities'

const paths = {
  gallery: '/gallery',
  archive: '/gallery/my-archive',
  recentlyDeleted: '/gallery/recently-deleted',
  bookmarks: '/gallery/bookmarks',
  album: '/gallery/album',
}

const filterOutMemory = (memoryList, memoryId) => memoryList?.filter((memory) => memory?.id !== memoryId)

/**
 * There is a case where the memory list is an array of memory ids as well.
 * In that case if the memory.id is undefined we will just check the memory.
 * This function also filters the deleted memory out of the stories' associated memories.
 */
const filterOutMemories = (memoryList, memoryIds) =>
  memoryList
    ?.filter((memory) => !memoryIds.includes(memory?.id ?? memory))
    ?.map((memory) => {
      if (memoryUtils.isText(memory) && memory?.associatedArtifacts?.artifact?.length > 0) {
        const updatedAssociatedMemories = memory?.associatedArtifacts?.artifact?.filter(
          (associatedMemory) => !memoryIds.includes(associatedMemory?.id)
        )

        return {
          ...memory,
          associatedArtifacts:
            updatedAssociatedMemories?.length === 0 ? undefined : { artifact: updatedAssociatedMemories },
        }
      }
      return memory
    })

/**
 * Removes duplicates from an array of ids.
 *
 * The usage of the Set object removes duplicates.
 */
const dedupeIdArray = (ids) => [...new Set(ids)]

/**
 * Converts an array of objects into an array of ids.
 */
const convertObjectsToIds = (objects) => objects?.map((obj) => obj?.id)

/**
 * Converts an array of objects into an array of unique ids.
 */
const convertObjectsToUniqueIds = (objects) => dedupeIdArray([...convertObjectsToIds(objects)])

/**
 * Removes the trailing slash from a string
 */
const removeTrailingSlash = (str = '') => (str.endsWith('/') ? str.slice(0, -1) : str)

/**
 * Returns the album id contained in the path. It finds the id by looking at the path
 * value immediately following `/gallery/album`.
 *
 * NOTE:  The reason for searching for the id following `/gallery/album` instead of just
 * looking for the id following the last `/` is because album URLs returned by the `/sitemap`
 * endpoint include the album title at the end. See examples below.
 *
 * Normal album URL:  /memories/gallery/album/1234
 * Sitemap album URL:  /memories/gallery/album/1234/my+album+title
 */
const getAlbumIdFromPath = (path = '') => {
  const match = path.match(/\/gallery\/album\/([^/]+)/)
  return match ? parseInt(match[1], 10) : null
}

/**
 * Determine if the path is for an album.
 */
const isAlbumPath = (path = '') => path.includes(paths.album)

/**
 * Determine if a collection contains an album id (numbers only)
 */
const isAlbumCollection = (collection) => /^\d+$/.test(collection)

/**
 * Returns the gallery path from the URI provided.
 */
const getPathFromUri = (uri = '') => uri.substring(uri.indexOf(paths.gallery))

/**
 * Positions the drag overlay under the cursor when dragging memories to the gallery explorer
 * This is a slightly modified version of snapCenterToCursor from @dnd-kit/modifiers to be slightly offset from center
 */
const snapDragOverlayToCursor = ({ activatorEvent, draggingNodeRect, transform }) => {
  if (draggingNodeRect && activatorEvent) {
    const activatorCoordinates = getEventCoordinates(activatorEvent)

    if (!activatorCoordinates) {
      return transform
    }

    const positionOffset = 12
    const offsetX = activatorCoordinates.x - draggingNodeRect.left - positionOffset
    const offsetY = activatorCoordinates.y - draggingNodeRect.top - positionOffset

    return {
      ...transform,
      x: transform.x + offsetX - draggingNodeRect.width / 2,
      y: transform.y + offsetY - draggingNodeRect.height / 2,
    }
  }

  return transform
}

export {
  convertObjectsToIds,
  convertObjectsToUniqueIds,
  dedupeIdArray,
  filterOutMemories,
  filterOutMemory,
  getAlbumIdFromPath,
  getPathFromUri,
  isAlbumCollection,
  isAlbumPath,
  paths,
  removeTrailingSlash,
  snapDragOverlayToCursor,
}
